import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, AlertTriangle, Clock, User, Calendar, Eye, XCircle } from 'lucide-react';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;

const MonitoreoPanel = ({ users }) => {
  const [capturas, setCapturas] = useState([]);
  const [desconexiones, setDesconexiones] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeView, setActiveView] = useState('capturas'); // 'capturas' o 'desconexiones'

  // Filtrar usuarios que pueden ser monitoreados (chatter y asistente)
  const monitoreableUsers = users.filter(u => u.role === 'chatter' || u.role === 'asistente');

  useEffect(() => {
    if (activeView === 'capturas') {
      fetchCapturas();
    } else {
      fetchDesconexiones();
    }
  }, [selectedUserId, selectedDate, activeView]);

  const fetchCapturas = async () => {
    setLoading(true);
    try {
      let url = `${API}/monitoreo/capturas/${selectedUserId === 'all' ? 'all' : selectedUserId}`;
      if (selectedDate) {
        url += `?fecha=${selectedDate}`;
      }
      
      const response = await axios.get(url, { withCredentials: true });
      setCapturas(response.data || []);
    } catch (error) {
      console.error('Error fetching capturas:', error);
      setCapturas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDesconexiones = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/monitoreo/desconexiones`, { withCredentials: true });
      let data = response.data || [];
      
      // Filtrar por usuario si es necesario
      if (selectedUserId !== 'all') {
        data = data.filter(d => d.user_id === selectedUserId);
      }
      
      // Filtrar por fecha si es necesario
      if (selectedDate) {
        data = data.filter(d => d.timestamp && d.timestamp.startsWith(selectedDate));
      }
      
      setDesconexiones(data);
    } catch (error) {
      console.error('Error fetching desconexiones:', error);
      setDesconexiones([]);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Usuario desconocido';
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('es-CO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  const getRazonLabel = (razon) => {
    const razones = {
      'inactividad_10_minutos': '⏰ Inactividad (10 min)',
      'no_persona_detectada': '👤 No se detectó persona',
      'cierre_manual': '🚪 Cierre manual'
    };
    return razones[razon] || razon;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border-red-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Eye className="w-6 h-6 text-red-400" />
            👁️ Panel de Monitoreo de Empleados
          </CardTitle>
          <p className="text-gray-400 text-sm mt-1">
            Visualiza las capturas de cámara y desconexiones por inactividad de los empleados (chatters y asistentes)
          </p>
        </CardHeader>
      </Card>

      {/* Controles */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Tabs */}
            <div className="flex gap-2">
              <Button
                onClick={() => setActiveView('capturas')}
                variant={activeView === 'capturas' ? 'default' : 'outline'}
                className={activeView === 'capturas' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'border-zinc-700 text-gray-300'}
              >
                <Camera className="w-4 h-4 mr-2" />
                Capturas
              </Button>
              <Button
                onClick={() => setActiveView('desconexiones')}
                variant={activeView === 'desconexiones' ? 'default' : 'outline'}
                className={activeView === 'desconexiones' 
                  ? 'bg-orange-600 hover:bg-orange-700' 
                  : 'border-zinc-700 text-gray-300'}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Desconexiones
              </Button>
            </div>

            {/* Filtro de usuario */}
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Todos los empleados" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Todos los empleados</SelectItem>
                {monitoreableUsers.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name} ({user.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Filtro de fecha */}
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white"
            />

            {/* Botón de actualizar */}
            <Button
              onClick={() => activeView === 'capturas' ? fetchCapturas() : fetchDesconexiones()}
              variant="outline"
              className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
            >
              🔄 Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contenido */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando datos...</p>
        </div>
      ) : activeView === 'capturas' ? (
        /* Vista de Capturas */
        <div>
          {capturas.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Camera className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No hay capturas para mostrar</p>
                <p className="text-gray-500 text-sm mt-2">
                  Las capturas aparecerán aquí cuando los empleados estén trabajando
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {capturas.map((captura, index) => (
                <Card 
                  key={captura.id || index} 
                  className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedImage(captura)}
                >
                  <CardContent className="p-3">
                    {/* Imagen */}
                    <div className="aspect-video bg-zinc-800 rounded-lg mb-3 overflow-hidden">
                      {captura.filepath ? (
                        <img 
                          src={`${API}/monitoreo/imagen/${captura.id}`}
                          alt="Captura"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-gray-500">📷 Imagen</span></div>';
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <Camera className="w-8 h-8 text-gray-600" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-white font-medium">{getUserName(captura.user_id)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>{formatDateTime(captura.timestamp)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {captura.persona_detectada ? (
                          <span className="text-green-400">✓ Persona detectada</span>
                        ) : (
                          <span className="text-red-400">✗ Sin persona</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Vista de Desconexiones */
        <div>
          {desconexiones.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No hay desconexiones registradas</p>
                <p className="text-gray-500 text-sm mt-2">
                  Aquí aparecerán las desconexiones automáticas por inactividad
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {desconexiones.map((desconexion, index) => (
                <Card 
                  key={desconexion.id || index} 
                  className="bg-zinc-900 border-zinc-800 border-l-4 border-l-orange-500"
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-orange-900/30 rounded-full">
                          <AlertTriangle className="w-6 h-6 text-orange-400" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">
                            {getUserName(desconexion.user_id)}
                          </p>
                          <p className="text-orange-400 text-sm">
                            {getRazonLabel(desconexion.razon)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDateTime(desconexion.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-900/30 rounded-full">
                <Camera className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Capturas Hoy</p>
                <p className="text-2xl font-bold text-white">{capturas.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-900/30 rounded-full">
                <XCircle className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Desconexiones</p>
                <p className="text-2xl font-bold text-white">{desconexiones.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-900/30 rounded-full">
                <User className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Empleados Monitoreados</p>
                <p className="text-2xl font-bold text-white">{monitoreableUsers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de imagen ampliada */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              📷 Captura de {selectedImage && getUserName(selectedImage.user_id)}
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="space-y-4">
              <div className="aspect-video bg-zinc-800 rounded-lg overflow-hidden">
                <img 
                  src={`${API}/monitoreo/imagen/${selectedImage.id}`}
                  alt="Captura ampliada"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Fecha y hora:</p>
                  <p className="text-white">{formatDateTime(selectedImage.timestamp)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Estado:</p>
                  <p className={selectedImage.persona_detectada ? 'text-green-400' : 'text-red-400'}>
                    {selectedImage.persona_detectada ? '✓ Persona detectada' : '✗ Sin persona detectada'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonitoreoPanel;
